<script setup lang="ts">
   import { SiteRouteNames, LmsRouteNames } from '~/types/types';
   import FeatureCard from '~/components/FeatureCard.vue';
   import { useOrganizationSchema } from '../composables/useOrganizationSchema';

   const showDevFeatures = useRuntimeConfig().public.showDevFeatures;

   // Nuxt auto-imports - these are available globally but TypeScript needs declarations
   declare const definePageMeta: (meta: any) => void;
   declare const useSeoMeta: (meta: any) => void;

   definePageMeta({
      name: SiteRouteNames.home,
      layout: 'website',
   });

   useSeoMeta({
      title: 'Home',
      description:
         'Supporting the global game through education, performance insight, and digital infrastructure.',
   });

   // Organization + WebSite structured data (JSON-LD) for SEO / entity understanding.
   useOrganizationSchema();
</script>

<template>
   <div>
      <!-- Hero Section - Company Positioning -->
      <section
         data-scroll-root
         class="relative h-[75vh] w-full overflow-hidden">
         <!-- Background Video -->
         <video
            autoplay
            muted
            loop
            playsinline
            disablepictureinpicture
            class="absolute inset-0 size-full object-cover">
            <source src="/video/home-page-video.mp4" type="video/mp4" />
         </video>

         <!-- Overlay for better text readability -->
         <div class="absolute inset-0 bg-black/40"></div>

         <!-- Scroll indicator -->
         <ScrollDownIndicator />

         <!-- Content -->
         <div class="relative z-10 flex size-full items-center px-5 lg:px-0">
            <div class="relative mx-auto w-full max-w-6xl rounded-lg p-5">
               <span
                  class="absolute inset-0 bg-blue-900 bg-opacity-50 blur-xl"></span>
               <div class="text-center text-white">
                  <!-- "INTERNATIONAL" is a single unbreakable word, so it clips rather
                       than wraps once it outgrows the line. At text-5xl it needs ~415px
                       against the ~295px a 375px phone leaves after the page and card
                       padding. -->
                  <h1
                     class="text-3xl font-bold uppercase text-brand-title sm:text-5xl md:text-6xl"
                     data-aos="fade-up"
                     data-aos-duration="800"
                     data-aos-once="true">
                     International Polo
                  </h1>
                  <p
                     class="mx-auto mt-6 max-w-3xl text-xl md:text-2xl"
                     data-aos="fade-up"
                     data-aos-delay="150"
                     data-aos-duration="800"
                     data-aos-once="true">
                     Supporting the global game through education, performance
                     insight, and digital infrastructure.
                  </p>
                  <div
                     class="mt-8 flex flex-wrap items-center justify-center gap-4"
                     data-aos="fade-up"
                     data-aos-delay="350"
                     data-aos-duration="800"
                     data-aos-once="true">
                     <NuxtLink
                        :to="{ name: SiteRouteNames.work }"
                        class="flex items-center gap-2 rounded-sm border-2 border-brand-primary bg-brand-title px-8 py-4 text-lg font-semibold uppercase text-white transition duration-300 hover:border-brand-primary hover:bg-brand-button">
                        <i
                           class="pi pi-th-large"
                           style="font-size: 1.25rem !important"></i>
                        <span>Our Work</span>
                     </NuxtLink>
                     <NuxtLink
                        :to="{ name: SiteRouteNames.platform }"
                        class="flex items-center gap-2 rounded-sm border-2 border-brand-primary bg-brand-title px-8 py-4 text-lg font-semibold uppercase text-white transition duration-300 hover:border-brand-primary hover:bg-brand-button">
                        <i
                           class="pi pi-arrow-right"
                           style="font-size: 1.25rem !important"></i>
                        <span>Enter Platform</span>
                     </NuxtLink>
                  </div>
               </div>
            </div>
         </div>
      </section>

      <!-- Our Role Section -->
      <section class="bg-gray-50 py-16">
         <div class="container mx-auto px-4">
            <div class="mx-auto max-w-4xl text-center">
               <p
                  class="text-lg leading-relaxed text-gray-700 md:text-xl"
                  data-aos="fade-up"
                  data-aos-duration="600"
                  data-aos-once="true">
                  International Polo builds digital and operational
                  infrastructure that supports organisations and participants
                  across the sport. By focusing on education environments,
                  performance analysis, and review tools, we help enable
                  clarity, consistency, and access within existing polo
                  structures. Our approach is collaborative, technology-led, and
                  designed to integrate alongside established systems and
                  workflows.
               </p>
            </div>
         </div>
      </section>

      <!-- Our Work Section -->
      <section class="bg-white py-16">
         <div class="container mx-auto px-4">
            <h2
               class="mb-8 text-center text-4xl font-bold uppercase text-brand-secondary"
               data-aos="fade-up"
               data-aos-duration="600"
               data-aos-easing="ease-in-out"
               data-aos-once="true"
               data-aos-offset="20">
               Our Work
            </h2>

            <div
               class="mx-auto grid max-w-6xl grid-cols-1 gap-8 md:grid-cols-2">
               <FeatureCard
                  icon="pi-desktop"
                  title="Polo Development Platform"
                  description="Structured digital learning for players, coaches, grooms, and umpires worldwide."
                  button-text="ENTER PLATFORM"
                  :button-disabled="false"
                  :delay="100"
                  :to="{ name: SiteRouteNames.platform }" />

               <FeatureCard
                  icon="pi-users"
                  title="International Polo Academy"
                  description="Education and development environments supporting learning across the sport."
                  button-text="VIEW PROGRAMMES"
                  :button-disabled="false"
                  :delay="200"
                  :to="{ name: SiteRouteNames.internationalPoloAcademy }">
                  <template #icon>
                     <img
                        src="/img/ipa-logo.png"
                        alt="International Polo Academy"
                        class="h-16 w-auto" />
                  </template>
               </FeatureCard>

               <FeatureCard
                  icon="pi-search"
                  title="Officiating Support"
                  description="Replay and review tools for umpire training and post-match review."
                  :delay="300">
                  <template #action>
                     <div
                        class="flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
                        <a
                           href="https://apps.apple.com/gb/app/polo-umpire/id1048064830"
                           target="_blank"
                           rel="noopener noreferrer"
                           class="flex items-center justify-center gap-2 rounded-sm border-2 border-brand-secondary bg-brand-secondary px-6 py-3 text-base font-semibold uppercase text-white transition duration-300 hover:border-brand-secondary hover:bg-brand-primary">
                           <i
                              class="pi pi-apple"
                              style="font-size: 1.5rem !important"></i>
                           <span>iOS App</span>
                        </a>
                        <a
                           href="https://play.google.com/store/apps/details?id=com.dartfish.android.polou&hl=en_GB&pli=1"
                           target="_blank"
                           rel="noopener noreferrer"
                           class="flex items-center justify-center gap-2 rounded-sm border-2 border-brand-secondary bg-brand-secondary px-6 py-3 text-base font-semibold uppercase text-white transition duration-300 hover:border-brand-secondary hover:bg-brand-primary">
                           <i
                              class="pi pi-android"
                              style="font-size: 1.5rem !important"></i>
                           <span>Android App</span>
                        </a>
                     </div>
                  </template>
               </FeatureCard>

               <FeatureCard
                  icon="pi-chart-line"
                  title="Performance Analysis"
                  description="Video, GPS, and structured review tools supporting teams and coaching staff."
                  button-text="LEARN MORE"
                  :button-disabled="false"
                  :delay="100"
                  :to="{ name: SiteRouteNames.performanceAnalysis }" />

               <FeatureCard
                  v-if="showDevFeatures"
                  icon="pi-id-card"
                  title="Coach Qualification"
                  description="Coaching education and certification pathways for polo coaches and instructors."
                  button-text="VIEW PROGRAMMES"
                  :button-disabled="false"
                  :delay="100"
                  :to="{
                     name: LmsRouteNames.courseCategory,
                     params: { slug: 'coach-instructor' },
                  }" />

               <FeatureCard
                  v-if="showDevFeatures"
                  icon="pi-verified"
                  title="Umpire Certification"
                  description="Structured certification and development pathways for polo umpires at all levels."
                  button-text="VIEW PROGRAMMES"
                  :button-disabled="false"
                  :delay="200"
                  :to="{
                     name: LmsRouteNames.courseCategory,
                     params: { slug: 'umpire-by-levels' },
                  }" />

               <FeatureCard
                  icon="pi-file"
                  title="Polo Report"
                  description="Data services supporting match reports, tournaments, results, and structured polo information."
                  :delay="200">
                  <template #icon>
                     <div
                        class="flex items-center justify-center rounded-lg px-4 py-3"
                        style="background-color: #153228">
                        <svg
                           xmlns="http://www.w3.org/2000/svg"
                           width="100"
                           height="34"
                           viewBox="0 0 100 34"
                           fill="none">
                           <path
                              d="M16.5851 2.6136C15.2805 1.92163 13.901 1.5426 12.5303 1.44122L11.4152 0L11.2169 1.428H11.2081L11.0538 2.618L11.0141 2.91771L9.43188 15.1527H12.0896C12.0896 15.1527 12.0587 13.7291 13.4999 12.1733C14.9323 10.6219 16.7438 9.37898 15.1659 6.5362L15.1439 6.49212C17.4402 8.12287 18.2644 11.1816 17.0171 13.8128C15.9813 15.9989 13.782 17.255 11.5078 17.1933H5.91476L6.75217 10.7144C6.93287 9.32168 6.22769 7.96861 4.98479 7.31631L3.22183 6.39075L0 30.1115H4.09009L5.30213 21.971H11.5122C15.6419 22.0679 19.6307 19.6659 21.3055 15.5317C23.2668 10.688 21.2085 5.0509 16.5895 2.60478L16.5851 2.6136Z"
                              fill="#719650"></path>
                           <path
                              d="M5.29748 21.9753L4.08984 30.1158H8.16671L9.37875 21.9753H5.29748Z"
                              fill="white"></path>
                           <path
                              d="M21.019 2.80746C19.6131 1.99209 18.1101 1.55135 16.6116 1.44116H12.5303C13.9054 1.54253 15.2806 1.92157 16.5852 2.61354C21.2041 5.05965 23.2668 10.6967 21.3011 15.5405C19.6263 19.6702 15.6376 22.0767 11.5078 21.9753H12.9182C14.4652 21.9753 15.8227 23.0066 16.237 24.5008L17.7928 30.1158H22.4735L19.9524 21.1159C22.3148 20.0889 24.3026 18.1761 25.3692 15.5713C27.2996 10.8598 25.4265 5.35495 21.019 2.80305V2.80746Z"
                              fill="white"></path>
                           <path
                              d="M33.8613 1.65259H39.7188C43.7251 1.65259 45.8319 4.01496 45.8319 6.92386C45.8319 9.83275 43.7251 12.1907 39.6968 12.1907H36.8099V15.3112H33.8613V1.65259ZM39.8466 9.66086C41.5964 9.66086 42.7335 8.44001 42.7335 6.91945C42.7335 5.39889 41.5964 4.17804 39.8686 4.17804H36.8143V9.65645H39.851L39.8466 9.66086Z"
                              fill="white"></path>
                           <path
                              d="M47.3691 10.0402C47.3691 6.98585 49.8549 4.56177 52.9974 4.56177C56.1399 4.56177 58.6477 6.98585 58.6477 10.0402C58.6477 13.0945 56.1619 15.5186 52.9974 15.5186C49.8329 15.5186 47.3691 13.0945 47.3691 10.0402ZM55.7609 10.0402C55.7609 8.39622 54.5797 7.17537 53.0018 7.17537C51.424 7.17537 50.2428 8.39622 50.2428 10.0402C50.2428 11.6842 51.424 12.905 53.0018 12.905C54.5797 12.905 55.7609 11.7018 55.7609 10.0402Z"
                              fill="white"></path>
                           <path
                              d="M60.4824 1.44116H63.3076V15.3069H60.4824V1.44116Z"
                              fill="white"></path>
                           <path
                              d="M65.1406 10.0402C65.1406 6.98585 67.6264 4.56177 70.7689 4.56177C73.9114 4.56177 76.4192 6.98585 76.4192 10.0402C76.4192 13.0945 73.9334 15.5186 70.7689 15.5186C67.6044 15.5186 65.1406 13.0945 65.1406 10.0402ZM73.5279 10.0402C73.5279 8.39622 72.3468 7.17537 70.7689 7.17537C69.191 7.17537 68.0099 8.39622 68.0099 10.0402C68.0099 11.6842 69.191 12.905 70.7689 12.905C72.3468 12.905 73.5279 11.7018 73.5279 10.0402Z"
                              fill="white"></path>
                           <path
                              d="M33.8613 16.4617H39.7188C43.7428 16.4617 45.8539 18.8241 45.8539 21.7506C45.8539 23.5003 44.946 25.1884 43.2624 26.0919L46.1096 30.1159H42.7158L40.6091 26.9117C40.208 26.9734 39.851 27.0174 39.4499 27.0174H36.8143V30.1159H33.8657V16.4573L33.8613 16.4617ZM39.8069 24.492C41.5346 24.492 42.7379 23.2491 42.7379 21.7506C42.7379 20.2521 41.5346 18.9695 39.829 18.9695H36.8143V24.492H39.8069Z"
                              fill="white"></path>
                           <path
                              d="M47.0566 24.8493C47.0566 21.8126 49.419 19.3708 52.513 19.3708C55.3778 19.3708 57.6345 21.817 57.6345 24.8096C57.6345 25.3165 57.5727 25.8233 57.5727 25.8233H49.9215C50.2608 27.2778 51.4597 27.9741 52.8524 27.9741C53.8661 27.9741 54.9195 27.6788 55.6996 27.1543L56.753 29.1774C55.5718 29.9575 54.2011 30.3365 52.8348 30.3365C49.7584 30.3365 47.061 28.3355 47.061 24.8581L47.0566 24.8493ZM54.9812 23.9413C54.7696 22.6147 53.7163 21.7288 52.4293 21.7288C51.1423 21.7288 50.1551 22.6147 49.8774 23.9413H54.9768H54.9812Z"
                              fill="white"></path>
                           <path
                              d="M59.4902 19.5778H62.1655V20.8427C63.0294 19.8114 64.2723 19.3662 65.5372 19.3662C68.4461 19.3662 70.3016 21.8123 70.3016 24.8226C70.3016 27.8329 68.4065 30.323 65.4976 30.323C64.2767 30.323 63.0735 29.8382 62.3154 28.9127V33.1482H59.4902V19.5734V19.5778ZM67.4148 24.849C67.4148 23.183 66.3394 21.9842 64.8012 21.9842C63.3688 21.9842 62.2934 23.1654 62.2934 24.849C62.2934 26.5327 63.3467 27.7139 64.8012 27.7139C66.3394 27.7139 67.4148 26.5106 67.4148 24.849Z"
                              fill="white"></path>
                           <path
                              d="M71.8184 24.8493C71.8184 21.7949 74.3041 19.3708 77.4466 19.3708C80.5891 19.3708 83.097 21.7949 83.097 24.8493C83.097 27.9036 80.6112 30.3277 77.4466 30.3277C74.2821 30.3277 71.8184 27.9036 71.8184 24.8493ZM80.2101 24.8493C80.2101 23.2053 79.0289 21.9844 77.451 21.9844C75.8732 21.9844 74.692 23.2053 74.692 24.8493C74.692 26.4932 75.8732 27.7141 77.451 27.7141C79.0289 27.7141 80.2101 26.5109 80.2101 24.8493Z"
                              fill="white"></path>
                           <path
                              d="M84.9297 19.5778H87.7108V21.6008C88.0061 20.7369 89.0815 19.3662 91.3998 19.3662V22.4426C88.9537 22.4426 87.7548 23.5577 87.7548 26.0655V30.1115H84.9297V19.5734V19.5778Z"
                              fill="white"></path>
                           <path
                              d="M94.0753 26.7266V21.8564H92.5371V19.5822H94.0753V17.5592L96.9005 16.929V19.5866H99.8094V21.8609H96.9005V26.687C96.9005 27.5949 97.3412 27.8902 97.848 27.8902C98.5224 27.8902 99.113 27.5112 99.1967 27.4495L99.9989 29.5342C99.3686 29.9573 98.4386 30.3363 97.3236 30.3363C95.2168 30.3363 94.0797 29.1155 94.0797 26.731L94.0753 26.7266Z"
                              fill="white"></path>
                        </svg>
                     </div>
                  </template>
                  <template #action>
                     <a
                        href="https://poloreport.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="flex items-center justify-center gap-2 rounded-sm border-2 border-brand-secondary bg-brand-secondary px-6 py-3 text-base font-semibold uppercase text-white transition duration-300 hover:border-brand-secondary hover:bg-brand-primary">
                        <span>VISIT POLOREPORT.COM</span>
                        <i
                           class="pi pi-arrow-right"
                           style="font-size: 1rem !important"></i>
                     </a>
                  </template>
               </FeatureCard>
            </div>
         </div>
      </section>

      <!-- Credibility Strip -->
      <section class="bg-gray-100 py-12">
         <div class="container mx-auto px-4">
            <p
               class="text-center text-2xl font-semibold text-gray-700"
               data-aos="fade-up"
               data-aos-duration="600"
               data-aos-once="true">
               Trusted by governing bodies, clubs, and players worldwide
            </p>
            <div
               class="mt-8 flex flex-wrap items-center justify-center gap-8"
               data-aos="fade-up"
               data-aos-delay="150"
               data-aos-duration="600"
               data-aos-once="true">
               <div class="flex w-[90px] items-center">
                  <img
                     src="/img/fip-logo.png"
                     alt="FIP - Federation of International Polo" />
               </div>
               <div class="flex w-[70px] items-center">
                  <img src="/img/ipa-logo.png" alt="International Polo" />
               </div>
               <a
                  href="https://www.aajpolo.com/"
                  target="_blank"
                  class="flex w-[90px] items-center overflow-hidden rounded-lg transition hover:opacity-80"
                  rel="noopener">
                  <img
                     src="/img/aajp-logo.jpg"
                     alt="AAJP - Argentine Association of Professional Polo Players" />
               </a>
               <div class="flex w-[300px] items-center">
                  <img
                     src="/img/uk-coaching-1st4sports-logo.png"
                     alt="UK Coaching 1st4Sport" />
               </div>
            </div>
         </div>
      </section>

      <!-- Partners CTA Section -->
      <section class="bg-white py-16">
         <div class="container mx-auto px-4">
            <div
               class="mx-auto max-w-4xl rounded-lg bg-brand-secondary p-12 text-center text-white"
               data-aos="fade-up"
               data-aos-duration="600"
               data-aos-once="true">
               <NuxtLink
                  :to="{ name: SiteRouteNames.about }"
                  class="inline-flex items-center gap-2 rounded-sm border-2 border-white bg-white px-8 py-4 text-lg font-semibold uppercase text-brand-title transition duration-300 hover:bg-brand-button hover:text-white">
                  <span>Learn more about us</span>
                  <i
                     class="pi pi-arrow-right"
                     style="font-size: 1.25rem !important"></i>
               </NuxtLink>
            </div>
         </div>
      </section>

      <!-- Contact Modals -->
      <ContactModals />
   </div>
</template>

<style scoped></style>
